/**
 * Centralized Revenue Calculation Service
 * Single source of truth for all revenue-related calculations and formatting
 */

/**
 * Configuration for currency formatting
 */
const CURRENCY_CONFIG = {
  locale: 'en-US',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
};

/**
 * Parse a currency string to a number
 * Handles various formats: "$1,234.56", "1234.56", "$1.2M", etc.
 * @param {string|number} value - The currency value to parse
 * @returns {number} The parsed numeric value
 */
export const parseCurrencyToNumber = (value) => {
  if (!value || value === 'N/A' || value === '') return 0;
  if (typeof value === 'number') return value;
  
  // Handle string values
  const stringValue = String(value).trim();
  if (stringValue === '' || stringValue === 'N/A') return 0;
  
  // Remove currency symbols, commas, and spaces
  const cleanValue = stringValue.replace(/[$,\s]/g, '');
  
  // Handle compact notation (K, M, B)
  if (cleanValue.includes('K')) {
    return parseFloat(cleanValue.replace('K', '')) * 1000;
  }
  if (cleanValue.includes('M')) {
    return parseFloat(cleanValue.replace('M', '')) * 1000000;
  }
  if (cleanValue.includes('B')) {
    return parseFloat(cleanValue.replace('B', '')) * 1000000000;
  }
  
  return parseFloat(cleanValue) || 0;
};

/**
 * Format a number as currency (USD)
 * @param {number|string} value - The value to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.compact - Whether to use compact notation (K, M, B)
 * @param {boolean} options.showCents - Whether to show cents (default: true)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, options = {}) => {
  const { compact = false, showCents = true } = options;
  
  const numValue = parseCurrencyToNumber(value);
  if (numValue === 0) return showCents ? '$0.00' : '$0';
  
  // Compact notation for large numbers
  if (compact) {
    if (numValue >= 1000000000) {
      return '$' + (numValue / 1000000000).toFixed(1) + 'B';
    } else if (numValue >= 1000000) {
      return '$' + (numValue / 1000000).toFixed(1) + 'M';
    } else if (numValue >= 1000) {
      return '$' + (numValue / 1000).toFixed(0) + 'K';
    }
    return '$' + numValue.toFixed(0);
  }
  
  // Full notation with proper formatting
  const formatConfig = {
    ...CURRENCY_CONFIG,
    style: 'currency',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0
  };
  
  return new Intl.NumberFormat(CURRENCY_CONFIG.locale, formatConfig).format(numValue);
};

/**
 * Calculate total revenue from an array of clients
 * @param {Array} clients - Array of client objects
 * @param {string} revenueField - Field name containing revenue (default: 'Total Revenue')
 * @returns {string} Formatted total revenue
 */
export const calculateTotalRevenue = (clients, revenueField = 'Total Revenue') => {
  if (!clients || clients.length === 0) return '$0.00';
  
  let total = 0;
  clients.forEach(client => {
    const revenueValue = client[revenueField];
    total += parseCurrencyToNumber(revenueValue);
  });
  
  return formatCurrency(total);
};

/**
 * Calculate filtered revenue with both total and last year breakdown
 * @param {Array} clients - Array of client objects
 * @param {Object} options - Calculation options
 * @param {boolean} options.compact - Use compact formatting
 * @param {string} options.totalField - Field for total revenue
 * @param {string} options.lastYearField - Field for last year revenue
 * @returns {Object} Object with total and lastYear revenue
 */
export const calculateFilteredRevenue = (clients, options = {}) => {
  const { 
    compact = false, 
    totalField = 'Total Revenue', 
    lastYearField = 'Last Booking Revenue' 
  } = options;
  
  if (!clients || clients.length === 0) {
    return { 
      total: compact ? '$0' : '$0.00', 
      lastYear: compact ? '$0' : '$0.00' 
    };
  }
  
  let totalRev = 0;
  let lastYearRev = 0;
  
  clients.forEach(client => {
    totalRev += parseCurrencyToNumber(client[totalField]);
    lastYearRev += parseCurrencyToNumber(client[lastYearField]);
  });
  
  return {
    total: formatCurrency(totalRev, { compact }),
    lastYear: formatCurrency(lastYearRev, { compact })
  };
};

/**
 * Calculate revenue statistics for a group of clients
 * @param {Array} clients - Array of client objects
 * @param {string} revenueField - Field containing revenue data
 * @returns {Object} Revenue statistics
 */
export const calculateRevenueStats = (clients, revenueField = 'Total Revenue') => {
  if (!clients || clients.length === 0) {
    return {
      total: 0,
      average: 0,
      median: 0,
      min: 0,
      max: 0,
      count: 0
    };
  }
  
  const revenues = clients
    .map(client => parseCurrencyToNumber(client[revenueField]))
    .filter(rev => rev > 0)
    .sort((a, b) => a - b);
  
  if (revenues.length === 0) {
    return {
      total: 0,
      average: 0,
      median: 0,
      min: 0,
      max: 0,
      count: 0
    };
  }
  
  const total = revenues.reduce((sum, rev) => sum + rev, 0);
  const average = total / revenues.length;
  const median = revenues.length % 2 === 0
    ? (revenues[revenues.length / 2 - 1] + revenues[revenues.length / 2]) / 2
    : revenues[Math.floor(revenues.length / 2)];
  
  return {
    total,
    average,
    median,
    min: revenues[0],
    max: revenues[revenues.length - 1],
    count: revenues.length
  };
};

/**
 * Sort clients by revenue
 * @param {Array} clients - Array of client objects
 * @param {Object} options - Sorting options
 * @param {string} options.field - Revenue field to sort by
 * @param {string} options.direction - 'asc' or 'desc'
 * @returns {Array} Sorted array of clients
 */
export const sortClientsByRevenue = (clients, options = {}) => {
  const { field = 'Total Revenue', direction = 'desc' } = options;
  
  return [...clients].sort((a, b) => {
    const aValue = parseCurrencyToNumber(a[field]);
    const bValue = parseCurrencyToNumber(b[field]);
    
    return direction === 'asc' ? aValue - bValue : bValue - aValue;
  });
};

/**
 * Filter clients by revenue range
 * @param {Array} clients - Array of client objects
 * @param {Object} range - Revenue range
 * @param {number} range.min - Minimum revenue
 * @param {number} range.max - Maximum revenue
 * @param {string} field - Revenue field to filter by
 * @returns {Array} Filtered array of clients
 */
export const filterClientsByRevenueRange = (clients, range, field = 'Total Revenue') => {
  const { min = 0, max = Infinity } = range;
  
  return clients.filter(client => {
    const revenue = parseCurrencyToNumber(client[field]);
    return revenue >= min && revenue <= max;
  });
};

/**
 * Get revenue breakdown by tier
 * @param {Array} clients - Array of client objects
 * @param {string} revenueField - Revenue field to analyze
 * @param {string} tierField - Tier field to group by
 * @returns {Object} Revenue breakdown by tier
 */
export const getRevenueBreakdownByTier = (clients, revenueField = 'Total Revenue', tierField = 'Tier') => {
  const breakdown = {};
  
  clients.forEach(client => {
    const tier = client[tierField] || 'Unknown';
    const revenue = parseCurrencyToNumber(client[revenueField]);
    
    if (!breakdown[tier]) {
      breakdown[tier] = {
        total: 0,
        count: 0,
        clients: []
      };
    }
    
    breakdown[tier].total += revenue;
    breakdown[tier].count += 1;
    breakdown[tier].clients.push(client);
  });
  
  // Calculate averages and format totals
  Object.keys(breakdown).forEach(tier => {
    const tierData = breakdown[tier];
    tierData.average = tierData.total / tierData.count;
    tierData.formattedTotal = formatCurrency(tierData.total);
    tierData.formattedAverage = formatCurrency(tierData.average);
  });
  
  return breakdown;
};

/**
 * Validate revenue value
 * @param {any} value - Value to validate
 * @returns {boolean} True if valid revenue value
 */
export const isValidRevenue = (value) => {
  if (!value || value === 'N/A' || value === '') return false;
  const numValue = parseCurrencyToNumber(value);
  return !isNaN(numValue) && numValue > 0; // Changed to > 0 to exclude zero values
};

// Legacy function names for backward compatibility
export const parseCurrency = parseCurrencyToNumber;
export const parseRevenueToNumber = parseCurrencyToNumber;

// Default export for convenience
const RevenueCalculationService = {
  parseCurrencyToNumber,
  formatCurrency,
  calculateTotalRevenue,
  calculateFilteredRevenue,
  calculateRevenueStats,
  sortClientsByRevenue,
  filterClientsByRevenueRange,
  getRevenueBreakdownByTier,
  isValidRevenue,
  // Legacy aliases
  parseCurrency: parseCurrencyToNumber,
  parseRevenueToNumber: parseCurrencyToNumber
};

export default RevenueCalculationService;
